<script setup lang="ts">
import type { NavbarNotificationItem } from "./useNavbarNotifications";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { ScrollDirection, useLazyLoadScroll } from "@verbleif/lib/src/composables/Scroll/useLazyLoadScroll";
import { inboxNotificationRoute } from "@verbleif/shared/src/composables/Notifications/v2/inboxNotificationLink";
import { truncateText } from "@verbleif/shared/src/composables/truncateText";
import { useFirebase } from "@verbleif/shared/src/composables/useFirebase";
import moment from "moment";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { useNavbarNotifications } from "./useNavbarNotifications";

const { embedded = false } = defineProps<{
  /** Rendered inside the phone account sheet instead of the desktop popper. */
  embedded?: boolean
}>();

const { t } = useI18n();
const router = useRouter();
const container: Ref<HTMLElement | null> = ref(null);
const { granted } = useFirebase();

const {
  isAllRead,
  items,
  loading,
  searchable,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  markAsRead,
  markOneAsRead,
  hasError,
  hasNotifications,
  refetch,
} = useNavbarNotifications();

const TRANSLATION_KEY = "navbar.dropdown";

function fromNow(sentAt: string) {
  const currentTime = moment();
  const sentAtTime = moment(sentAt);
  const diffInSeconds = currentTime.diff(sentAtTime, "seconds");

  if (diffInSeconds < 60) {
    return t(`${TRANSLATION_KEY}.just_now`);
  } else {
    return sentAtTime.from(currentTime);
  }
}

async function notificationLocation(notification: NavbarNotificationItem) {
  if (!notification.target) {
    toast.error(t(`${TRANSLATION_KEY}.no_path`));
    return;
  }

  await markOneAsRead(notification);
  router.push(inboxNotificationRoute(notification.target));
}

async function handleMarkAsRead(notification: NavbarNotificationItem, event: Event) {
  event.stopPropagation();
  await markOneAsRead(notification);
}

useLazyLoadScroll(
  container,
  () => fetchNextPage(),
  hasNextPage,
  isFetchingNextPage,
  ScrollDirection.BOTTOM,
);
</script>

<template>
  <div class="flex flex-1 flex-col text-lg leading-5 text-gray-800 dark:text-white" :class="embedded ? 'h-full w-full' : 'h-[500px] w-[361px] max-w-[calc(100vw-2rem)]'">
    <div class="mx-5 mt-4 flex flex-col gap-4">
      <div class="flex justify-between items-center gap-3">
        <div v-if="!embedded" class="text-lg font-bold">
          {{ t(`${TRANSLATION_KEY}.notifications`) }}
        </div>
        <button
          type="button"
          class="rounded-xs text-sm focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden"
          :class="hasNotifications && !isAllRead
            ? 'cursor-pointer text-primary-600 hover:text-primary-700'
            : 'cursor-not-allowed text-gray-300 dark:text-gray-600'"
          :disabled="!hasNotifications || isAllRead"
          @click="markAsRead()"
        >
          {{ t(`${TRANSLATION_KEY}.mark_all_read`) }}
        </button>
      </div>
      <div v-if="!granted" class="flex items-center justify-between rounded-lg bg-red-600/10 text-sm font-bold text-red-600 dark:text-red-400" role="status">
        <div class="flex items-center">
          <VIcon class="ml-4 mr-3" name="pin" aria-hidden="true" />
          <div>{{ t(`${TRANSLATION_KEY}.push_notification`) }}</div>
        </div>
        <RouterLink
          v-close-popper.all
          class="m-4 rounded-xs focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden"
          to="/settings/my-account/notifications"
          :aria-label="t(`${TRANSLATION_KEY}.notifications`)"
        >
          <VIcon name="external-link" aria-hidden="true" />
        </RouterLink>
      </div>
      <div>
        <VInput
          v-model="searchable.searchValue.value"
          icon-left="search"
          icon-right="x"
          :placeholder="t(`${TRANSLATION_KEY}.search`)"
          @on-right-icon-click="searchable.searchValue.value = ''"
        />
      </div>
    </div>
    <div v-if="hasError" class="flex flex-col items-center justify-center p-8 text-center flex-1 overflow-hidden">
      <VIcon name="triangle-alert" class="text-4xl text-red-500 mb-4" aria-hidden="true" />
      <div class="text-gray-600 dark:text-gray-400" role="alert">
        {{ t(`${TRANSLATION_KEY}.error_loading`) }}
      </div>
      <VButton variant="secondary" class="mt-4" @click="refetch">
        {{ t(`${TRANSLATION_KEY}.try_again`) }}
      </VButton>
    </div>
    <div
      v-else-if="!loading && !hasNotifications && !searchable.searchValue.value"
      class="flex flex-col items-center justify-center p-8 text-center flex-1 overflow-hidden"
    >
      <VIcon name="bell" class="text-4xl text-gray-400 mb-4" aria-hidden="true" />
      <div class="text-gray-600 dark:text-gray-400">
        {{ t(`${TRANSLATION_KEY}.no_notifications`) }}
      </div>
    </div>
    <div
      v-else-if="!loading && !hasNotifications && searchable.searchValue.value"
      class="flex flex-col items-center justify-center p-8 text-center flex-1 overflow-hidden"
    >
      <VIcon name="search" class="text-4xl text-gray-400 mb-4" aria-hidden="true" />
      <div class="text-gray-600 dark:text-gray-400">
        {{ t(`${TRANSLATION_KEY}.no_search_results`) }}
      </div>
    </div>
    <!-- this is the list of notifications -->
    <div v-else class="flex flex-1 overflow-hidden">
      <ul ref="container" class="flex flex-col w-full overflow-y-auto mt-3 text-gray-800 dark:text-white" :class="{ 'max-h-[400px]': !embedded }">
        <li
          v-for="(notification, index) in items"
          :key="'skeleton' in notification ? `skeleton-${index}` : notification.id"
          class="hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <template v-if="'skeleton' in notification">
            <div
              class="flex pl-2 pr-3 py-3 rounded-lg group"
            >
              <div class="flex h-6 w-5 items-center justify-center">
                <VSkeleton
                  v-if="index % 2 === 0"
                  class="mb-1"
                  :dynamic-width="false"
                  :width="8"
                  :height="8"
                  width-unit="px"
                  height-unit="px"
                />
              </div>

              <div class="flex flex-col w-full">
                <div class="flex justify-between">
                  <VSkeleton class="mb-2" :width="100" :height="16" width-unit="px" height-unit="px" />
                  <VSkeleton class="mb-1" :width="100" :height="12" width-unit="px" height-unit="px" />
                </div>
                <div class="flex justify-between">
                  <div class="flex flex-col">
                    <VSkeleton class="mb-1" :width="200" :height="16" width-unit="px" height-unit="px" />
                    <VSkeleton class="mb-1" :width="150" :height="16" width-unit="px" height-unit="px" />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="group relative flex h-20 flex-col">
              <div class="mx-5">
                <VDivider no-margins />
              </div>
              <button
                v-close-popper.all="notification.target !== null"
                type="button"
                class="flex w-full cursor-pointer rounded-lg py-3 pr-3 text-left focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden focus-visible:ring-inset"
                @click="notificationLocation(notification)"
              >
                <span class="flex h-4 w-5 shrink-0 items-center justify-center">
                  <span v-if="!notification.readAt" class="h-2 w-2 rounded-full bg-primary-600" />
                </span>
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="flex justify-between gap-2">
                    <span :class="notification.readAt ? '' : 'font-bold'" class="mb-1 text-sm leading-4 dark:text-white">
                      {{ notification.title }}
                    </span>
                    <span v-if="notification.sentAt" class="shrink-0 text-xs text-gray-400 dark:text-gray-200">
                      {{ fromNow(notification.sentAt) }}
                    </span>
                  </span>
                  <span v-if="notification.subject" class="w-fit">
                    <VTooltip
                      theme="verbleif-tooltip-dark"
                      placement="top"
                    >
                      <template #content>
                        {{ notification.target?.type === 'task'
                          ? t(`${TRANSLATION_KEY}.view_task`)
                          : t(`${TRANSLATION_KEY}.view_report`)
                        }}
                      </template>
                      <template #reference>
                        <span class="text-sm group-hover:text-primary-600">
                          {{ notification.subject }}
                        </span>
                      </template>
                    </VTooltip>
                  </span>
                  <span class="w-3/4 text-sm leading-4 font-normal text-gray-500 dark:text-gray-300">
                    {{ truncateText(notification.message ?? '', 65) }}
                  </span>
                </span>
              </button>
              <VTooltip
                v-if="!notification.readAt"
                class="absolute right-3 bottom-3"
                theme="verbleif-tooltip-dark"
                placement="top"
              >
                <template #content>
                  {{ t(`${TRANSLATION_KEY}.mark_as_read`) }}
                </template>
                <template #reference>
                  <button
                    type="button"
                    class="flex rounded-full text-sm text-primary-600 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden"
                    :aria-label="t(`${TRANSLATION_KEY}.mark_as_read`)"
                    @click="handleMarkAsRead(notification, $event)"
                  >
                    <VIcon name="circle-check" aria-hidden="true" />
                  </button>
                </template>
              </VTooltip>
            </div>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>
